/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { shallowEqual, useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { ECOMMERCE_MAIN_MERCHANT_ID_KEY } from "ecommerce/constants"
import { useGetEcommerceMerchantID } from "ecommerce/hooks/useGetEcommerceMerchantID"
import { FoodmaxapipbMerchant } from "generated/foodmaxapi.type"
import { useGetMerchantDetailQuery } from "queries/merchantApi"
import { AppState } from "store/store.type"

interface UseMerchantDetailResult {
  merchantGroupID: string
  merchantID: string
  isMerchantLevelUser: boolean
  isMainMerchant: boolean
  countryID: number
  merchant: FoodmaxapipbMerchant | undefined
  isLoading: boolean
  isError: boolean
  fetchedMerchantID: string
}

export const useMerchantDetail = (): UseMerchantDetailResult => {
  const { storeID: routeStoreID } = useParams<{ storeID: string }>()
  const { outlets, merchantGroupID } = useSelector(
    (state: AppState) => ({
      outlets: state.menu.outlets,
      merchantGroupID: state.menu.merchantGroupID,
    }),
    shallowEqual,
  )

  const {
    merchantID: resolvedMerchantID,
    isMerchantLevelUser,
  } = useGetEcommerceMerchantID()

  // Validate `:storeID` param against the resolved merchantID.
  const isMerchantIDMismatch = routeStoreID !== resolvedMerchantID

  // In the multi-location view, "main" represents the merchantGroup-level user and has no
  // specific store-level merchantID. We fall back to the first outlet's merchantID to fetch
  // shared data like countryID.
  const isMainMerchant =
    routeStoreID === ECOMMERCE_MAIN_MERCHANT_ID_KEY &&
    resolvedMerchantID === ECOMMERCE_MAIN_MERCHANT_ID_KEY
  const fetchedMerchantID =
    (isMainMerchant ? outlets?.[0]?.merchantID : routeStoreID) ?? ""

  const { data: merchantData, isLoading, isError } = useGetMerchantDetailQuery(
    { merchantID: fetchedMerchantID },
    {
      // Keep `isMerchantIDMismatch` here: a skipped query never reports
      // `isLoading`, which is what lets `MerchantDetailGuard`'s
      // `renderWhileLoading` (used by `ProductsList`) render children early
      // without ever exposing an unvalidated `:storeID`. Drop it and
      // GetMerchant becomes the validator instead of this check.
      skip: !fetchedMerchantID || isMerchantIDMismatch,
    },
  )

  return {
    merchantGroupID,
    merchantID: routeStoreID,
    isMerchantLevelUser,
    isMainMerchant,
    fetchedMerchantID,
    countryID: merchantData?.countryID ?? 0,
    merchant: merchantData?.ID === routeStoreID ? merchantData : undefined,
    isLoading,
    isError: isError || isMerchantIDMismatch || !fetchedMerchantID,
  }
}
